import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import ParentDashboard from "@/pages/ParentDashboard";
import { ScoreTrendChart } from "@/components/ScoreTrendChart";
import TargetProdiCard from "@/components/TargetProdiCard";
import OnboardingCard from "@/components/OnboardingCard";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Compass, ClipboardCheck, ListChecks, FileText, ArrowRight, TrendingUp, Target, Sparkles, Copy, Users, Download, Receipt, AlertTriangle, Signpost, UserRound, CheckCircle2 } from "lucide-react";

const tanggalOnly = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
};

function AdminHome({ name }) {
  const cards = [
    { to: "/admin/soal", label: "Kelola Soal", desc: "Atur bank soal asesmen", icon: ClipboardCheck },
    { to: "/admin/tryout-upload", label: "Upload TryOut", desc: "Unggah & cocokkan hasil TryOut batch", icon: FileText },
    { to: "/admin/asesmen-upload", label: "Upload Asesmen", desc: "Unggah asesmen mitra massal", icon: Sparkles },
    { to: "/admin/latihan-upload", label: "Recognize & Postes", desc: "Unggah diagnostik awal & postes harian", icon: FileText },
    { to: "/admin/kelola-upload", label: "Kelola Upload", desc: "Hapus PDF yang salah diunggah", icon: FileText },
    { to: "/admin/laporan-bulanan", label: "Laporan Bulanan", desc: "Generate, edit & publikasikan laporan siswa", icon: FileText },
    { to: "/admin/akun", label: "Kelola Akun", desc: "Lihat profil siswa, impor, reset & hapus akun", icon: Users },
  ];
  return (
    <AppShell>
      <div className="max-w-4xl mx-auto" data-testid="admin-home">
        <p className="text-[#5C5F5C]">Panel Admin</p>
        <h1 className="font-heading font-black text-3xl text-[#1A1C1A] mb-6">Halo, {name || "Admin"} 👋</h1>
        <div className="grid sm:grid-cols-2 gap-4">
          {cards.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`admin-card-${c.to.split("/").pop()}`}
              className="clay-card p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-2">
                <span className="h-11 w-11 rounded-xl bg-[#E25E3E]/10 text-[#E25E3E] grid place-items-center"><c.icon className="h-5 w-5" /></span>
                <h3 className="font-heading font-bold text-lg text-[#1A1C1A]">{c.label}</h3>
              </div>
              <p className="text-sm text-[#5C5F5C]">{c.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [onboardHidden, setOnboardHidden] = useState(false);
  const [monthlyReady, setMonthlyReady] = useState(false);
  const isParent = user?.role === "orangtua";
  const isAdmin = user?.role === "admin";

  const dismissOnboard = () => { setOnboardHidden(true); api.post("/me/onboarded").catch(() => {}); };

  useEffect(() => {
    if (isParent || isAdmin) return;
    api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/student/invite-code").then((r) => setInviteCode(r.data.code)).catch(() => {});
    api.get("/report/monthly/status").then((r) => setMonthlyReady(!!r.data.available)).catch(() => {});
  }, [isParent]);

  if (isAdmin) return <AdminHome name={user?.name} />;
  if (isParent) return <ParentDashboard />;

  const progress = data?.progress || { selesai: 0, total: 4, persen: 0 };
  const topTypes = data?.assessment?.scores?.tipe_dominan || [];
  const rec = data?.assessment?.scores?.rekomendasi?.[0];

  const copyCode = () => { navigator.clipboard.writeText(inviteCode); toast.success("Kode disalin"); };
  const downloadPdf = async () => {
    try { await downloadFile("/report/pdf", `Rapor-${user?.name || "Siswa"}.pdf`); toast.success("Rapor PDF diunduh"); }
    catch { toast.error("Unduh gagal — selesaikan asesmen dulu ya"); }
  };
  const downloadMonthly = async () => {
    if (!monthlyReady) return;
    try { await downloadFile("/report/monthly/pdf", `Laporan-Bulanan-${user?.name || "Siswa"}.pdf`); toast.success("Laporan Bulanan diunduh"); }
    catch { toast.error("Laporan bulanan belum tersedia"); }
  };

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#5C5F5C]">Halo, selamat datang 👋</p>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">{user?.name}</h1>
        </div>
        <button onClick={downloadPdf} data-testid="download-pdf-btn" className="px-5 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium hover:bg-[#1A1C1A]/5 transition-colors flex items-center gap-2">
          <Download className="h-4 w-4" /> Unduh Rapor PDF
        </button>
        <button onClick={downloadMonthly} disabled={!monthlyReady} data-testid="download-monthly-btn"
          title={monthlyReady ? "Unduh Laporan Bulanan Progress" : "Laporan bulanan belum dipublikasikan admin"}
          className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${monthlyReady ? "bg-[#E25E3E] text-white hover:bg-[#C94F31]" : "border-2 border-[#E5E0D8] text-[#C7C2B8] cursor-not-allowed"}`}>
          <FileText className="h-4 w-4" /> Laporan Bulanan
        </button>
        <Link to="/riwayat" data-testid="riwayat-link" className="px-5 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium hover:bg-[#1A1C1A]/5 transition-colors flex items-center gap-2">
          <Receipt className="h-4 w-4" /> Riwayat Pembelian
        </Link>
      </div>

      {data && !user?.onboarded && !onboardHidden && <OnboardingCard data={data} onDismiss={dismissOnboard} />}

      {data?.credit?.expiring_soon && (
        <div className="clay-card p-5 mb-6 flex flex-wrap items-center justify-between gap-4 bg-[#E25E3E]/10 border-[#E25E3E]/40" data-testid="credit-expiry-banner">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-xl bg-[#E25E3E] text-white grid place-items-center"><AlertTriangle className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-[#1A1C1A]">{data.credit.credits} kredit akan segera kedaluwarsa</p>
              <p className="text-xs text-[#5C5F5C]">Gunakan sebelum {tanggalOnly(data.credit.next_expiry)}{data.credit.days_left >= 0 ? ` (± ${data.credit.days_left} hari lagi)` : ""}. Kredit yang lewat masa berlaku akan hangus.</p>
            </div>
          </div>
          <Link to="/tryout" data-testid="pakai-kredit-btn" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors flex items-center gap-2">Pakai Sekarang <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}

      {inviteCode && (
        <div className="clay-card p-5 mb-6 flex flex-wrap items-center justify-between gap-4 bg-[#F4B942]/15 border-[#F4B942]/40" data-testid="invite-code-card">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-xl bg-[#F4B942] text-[#1A1C1A] grid place-items-center"><Users className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-[#1A1C1A]">Kode Undangan Orang Tua</p>
              <p className="text-xs text-[#5C5F5C]">Beri kode ini ke orang tua agar bisa memantau perjalananmu.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-heading font-black text-2xl tracking-widest text-[#1A1C1A] bg-white px-4 py-2 rounded-xl border border-[#E5E0D8]" data-testid="invite-code-value">{inviteCode}</span>
            <button onClick={copyCode} data-testid="copy-code-btn" className="p-2.5 rounded-xl bg-[#1A1C1A] text-white hover:opacity-90 transition-opacity"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <FlowStepper data={data} progress={progress} />

      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
        {/* North star: posisi anak */}
        <div className="md:col-span-8 lg:col-span-12 clay-card p-7 bg-[#1A1C1A] text-white border-[#1A1C1A]">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-3"><Target className="h-4 w-4" /> Posisi Kamu Saat Ini</div>
          {data?.assessment ? (
            <>
              <p className="text-white/90 leading-relaxed">{data.assessment.narrative?.ringkasan}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {topTypes.map((t) => (
                  <span key={t.kode} className="px-3 py-1.5 rounded-full bg-white/10 text-sm">{t.label}</span>
                ))}
              </div>
              <Link to="/profilku" data-testid="lihat-hasil-btn" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors">
                Lihat Hasil Lengkap <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          ) : (
            <>
              <p className="text-white/80 leading-relaxed">Kamu belum mengikuti Asesmen Potensi Diri. Ikuti asesmen (maks 30 soal) untuk mendapat gambaran presisi tentang karakter, kebiasaan belajar, dan kekuatan kognitifmu.</p>
              <Link to="/asesmen" data-testid="mulai-asesmen-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] transition-colors">
                <Sparkles className="h-4 w-4" /> Mulai Asesmen Potensi Diri
              </Link>
            </>
          )}
        </div>

        {/* Rekomendasi bidang */}
        <div className="md:col-span-8 lg:col-span-7 clay-card p-7">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#5C5F5C] mb-4"><Compass className="h-4 w-4" /> Bidang Paling Sesuai</div>
          {rec ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-heading font-black text-2xl text-[#1A1C1A]">{rec.nama}</h3>
                <p className="text-sm text-[#5C5F5C] mt-1">{rec.jurusan?.join(" · ")}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading font-black text-3xl" style={{ color: rec.warna }}>{rec.match}%</div>
                <div className="text-xs text-[#5C5F5C]">kecocokan</div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#5C5F5C]">Selesaikan asesmen untuk melihat bidang yang paling sesuai denganmu.</p>
          )}
        </div>

        {/* TryOut terbaik */}
        <div className="md:col-span-4 lg:col-span-5 clay-card p-7">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#5C5F5C] mb-4"><TrendingUp className="h-4 w-4" /> Skor TryOut Terbaik</div>
          {data?.tryout_terbaik != null ? (
            <>
              <div className="font-heading font-black text-4xl text-[#4A6B53]">{data.tryout_terbaik}</div>
              <p className="text-sm text-[#5C5F5C] mt-1">dari {data.tryout_count} percobaan · <Link to="/tryout" className="text-[#E25E3E] font-medium">lihat semua</Link></p>
              <p className="text-xs text-[#5C5F5C]">≈ {Math.round(200 + (data.tryout_terbaik / 100) * 800)} skala UTBK (estimasi)</p>
            </>
          ) : (
            <p className="text-sm text-[#5C5F5C]">Belum ada TryOut. <Link to="/tryout" className="text-[#E25E3E] font-medium">Mulai sekarang →</Link></p>
          )}
        </div>

        {/* Grafik tren skor TryOut */}
        {data?.tryout_trend?.length > 0 && (
          <div className="md:col-span-8 lg:col-span-12 clay-card p-7" data-testid="score-trend-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#5C5F5C] mb-4"><TrendingUp className="h-4 w-4" /> Tren Skor TryOut</div>
            <ScoreTrendChart data={data.tryout_trend} />
          </div>
        )}

        {/* Target prodi UTBK */}
        {data && <TargetProdiCard targets={data.prodi_targets || []} utbkBest={data.utbk_best ?? null} onChange={() => api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => {})} />}
      </div>
    </AppShell>
  );
}

function FlowStepper({ data, progress }) {
  const flow = [
    { label: "Asesmen", to: "/asesmen", icon: ClipboardCheck, done: !!data?.assessment },
    { label: "Profilku", to: "/profilku", icon: UserRound, done: !!data?.assessment },
    { label: "Jelajahi Bidang", to: "/jelajahi-bidang", icon: Compass, done: !!data?.journey?.bidang_minat },
    { label: "Susun Langkah", to: "/susun-langkah", icon: ListChecks, done: !!(data?.journey?.rencana?.length || data?.journey?.mapel_pilihan?.length) },
    { label: "TryOut", to: "/tryout", icon: FileText, done: data?.tryout_count > 0 },
    { label: "Info Jalur", to: "/info-jalur", icon: Signpost, optional: true, done: false },
  ];
  const activeIdx = flow.findIndex((f) => !f.done && !f.optional);
  const persen = progress?.persen ?? 0;
  return (
    <div className="clay-card p-6 mb-6" data-testid="flow-stepper">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading font-black text-xl text-[#1A1C1A]">Alur Perjalananmu</h2>
          <span className="text-xs text-[#5C5F5C]">Progres mencakup seluruh perjalanan — termasuk TryOut tiap tahun, jadi selalu ada langkah berikutnya (tidak berhenti di 100%).</span>
        </div>
        <div className="w-full sm:w-64" data-testid="journey-progress">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-[#5C5F5C]">Progres Perjalanan</span>
            <span className="font-heading font-black text-lg text-[#E25E3E]">{persen}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-[#F0ECE4] overflow-hidden">
            <div className="h-full bg-[#E25E3E] rounded-full transition-all" style={{ width: `${persen}%` }} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {flow.map((f, i) => {
          const active = i === activeIdx;
          const status = f.done ? "selesai" : f.optional ? "opsional" : active ? "aktif" : "belum";
          const box = {
            selesai: "bg-[#4A6B53] text-white border-[#4A6B53]",
            aktif: "bg-white border-[#E25E3E] ring-2 ring-[#E25E3E]/30",
            belum: "bg-white border-[#E5E0D8]",
            opsional: "bg-[#F4B942]/10 border-[#F4B942]/40",
          }[status];
          const badge = {
            selesai: "Selesai ✓", aktif: "Aktif sekarang", opsional: "Opsional", belum: "Belum dimulai",
          }[status];
          return (
            <Link key={f.label} to={f.to} data-testid={`flow-step-${i}`}
              className={`relative rounded-2xl border p-4 transition-transform hover:-translate-y-0.5 ${box}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`h-9 w-9 rounded-xl grid place-items-center ${f.done ? "bg-white/20 text-white" : active ? "bg-[#E25E3E] text-white" : "bg-[#F0ECE4] text-[#5C5F5C]"}`}>
                  {f.done ? <CheckCircle2 className="h-5 w-5" /> : <f.icon className="h-5 w-5" />}
                </span>
                <span className={`text-[10px] font-bold ${f.done ? "text-white/80" : active ? "text-[#E25E3E]" : "text-[#5C5F5C]"}`}>{i + 1}</span>
              </div>
              <p className={`font-heading font-bold text-sm ${f.done ? "text-white" : "text-[#1A1C1A]"}`}>{f.label}</p>
              <span className={`mt-1 inline-block text-[11px] font-medium ${f.done ? "text-white/80" : active ? "text-[#E25E3E]" : status === "opsional" ? "text-[#8a6d16]" : "text-[#5C5F5C]"}`}>{badge}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
